import React, {Component} from 'react';
import PropTypes from 'prop-types';

/*
    This is a wrapper component implementing basic drag and drop.
    It uses an absolutely position element, and updates the elements top and left CSS values.
*/

const touchmove_listener_options = {
    /*
        See here for passive vs active listeners: 
        https://developers.google.com/web/updates/2016/06/passive-event-listeners
        On mobile chrome, touchmove is passive by default, which prevents us
        from preventing pull-to-refresh
    */
    passive: false,
}

export default class Draggable extends Component {
    constructor(props) {
        super(props);

        this.state = {
            drag_location: props.initial_coords,
            is_dragging: false,
            initial_offset: null,
        }
    }

    getCurrentLocation() {
        return this.state.is_dragging ? this.state.drag_location : this.props.initial_coords;
    }

    // While dragging, you might move your mouse quickly off of this element, before we can update it's position.
    // For that reason, we need to attach a move listener directly to the window.
    componentDidMount() {
        window.addEventListener('mousemove', this.onMouseMove);
        window.addEventListener('touchmove', this.onMouseMove, touchmove_listener_options);
    }
    componentWillUnmount() {
        window.removeEventListener('mousemove', this.onMouseMove);
        window.removeEventListener('touchmove', this.onMouseMove, touchmove_listener_options);
    }

    onMouseDown = (event) => {
        if (!this.props.draggable) {
            return;
        }

        /*
            Calculate "initial_offset", which involves several things:
                - cursor position relative to the Draggable element
                - the Draggable element's offsetParent's position relative to view port

            We'll use this in subsequent event handlers to convert from view port coordinates
            back to appropriate left/top values for the Draggable element.
        */
        var position = this.getClientPosition(event);
        var initial_offset = {
            x: position.x - this.props.initial_coords.left,
            y: position.y - this.props.initial_coords.top,
        };

        this.setState({
            drag_location: this.props.initial_coords,
            is_dragging: true,
            initial_offset: initial_offset,
        });

        window.addEventListener('mouseup', this.finish_dragging, {once: true});

        // 'touchstart' isn't always followed by 'touchend'
        // for a quick touch, it will fire 'touchcancel' instead
        window.addEventListener('touchend', this.finish_dragging, {once: true});
        window.addEventListener('touchcancel', this.finish_dragging, {once: true});
        
        this.props.on_drag_start(this.props.initial_coords, this.element);

        /* 
            Prevent page over-scroll (mobile safari)
            If we want to disable over-scroll entirely, we might want to add event listener
            directly to the window and always preventDefault
        */
        event.preventDefault();
    }
    finish_dragging = () => {
        // this might fire multiple times when canceling the same drag
        // be sure to get updated state
        this.setState((previous_state) => {
            if (!previous_state.is_dragging) {
                return {};
            }
            this.props.on_drag_end(previous_state.drag_location, this.element);
            return {is_dragging: false};
        });
    }
    onMouseMove = (event) => {
        if (!this.state.is_dragging) {
            return;
        }
        var position = this.getClientPosition(event);
        var location = {
            left: position.x - this.state.initial_offset.x,
            top: position.y - this.state.initial_offset.y,
        }; 

        this.setState({
            drag_location: location,
        });

        this.props.on_drag(location, this.element);

        /*
            This isn't really needed on desktop, but on mobile touchmove triggers
            page reload. We want to prevent that.
            Alternatively, we could disable pull-to-reload globally
            (even when not dragging a robot).
        */
        event.preventDefault();
    }
    getClientPosition(event) {
        return (event.touches ? {
            x: event.touches[0].clientX,
            y: event.touches[0].clientY,
        } : {
            x: event.clientX,
            y: event.clientY,
        });
    }

    render() {
        const {transition, on_transition_end, children} = this.props;
        const location = this.getCurrentLocation();

        return <div 
            ref={(div) => {
                if (div) {
                    // Prevent html drag and drop of all elements inside this div.
                    // Without this, if you place elements which are normally draggable (ie. images) 
                    // inside of this component, you'll starting dragging that element (ie. native dragging)
                    // instead of dragging this component
                    div.addEventListener('dragstart', function(e) {e.preventDefault(); e.stopPropagation();}, true);

                    // store reference to div
                    this.element = div;
                }
            }}
            style={{
                position: 'absolute',
                left: location.left + 'px',
                top: location.top + 'px',
                transition: this.state.is_dragging ? null : transition,
            }}
            onMouseDown={this.onMouseDown}
            onTouchStart={this.onMouseDown}
            onTransitionEnd={on_transition_end}
        >
            {children}
        </div>
    }
}
Draggable.propTypes = {
    /*
        All coordinates here are objects of form {left: number, top: number}
        These coordinates refer to the Draggable element's position within its offsetParent
        (ie. correspond to CSS left and top values).
    */
    initial_coords: PropTypes.object.isRequired,

    draggable: PropTypes.bool.isRequired,

    /*
        These callbacks will called with arguments (current_coordinates, element)
    */
    on_drag_start: PropTypes.func,
    on_drag: PropTypes.func,
    on_drag_end: PropTypes.func,

    /*
        The css value to use for the 'transition' property.
        This is only applied when draggable = false.
        You can use this to animate transitions between different values of initial_coords.
        Set to 'none' to prevent default transition.
    */
    transition: PropTypes.string,
    on_transition_end: PropTypes.func,
}
Draggable.defaultProps = {
    transition: 'all 0.25s ease-in',
}


// WEBPACK FOOTER //
// ./src/Draggable.js